import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { flatten } from 'lodash/fp';
import {
  deselectAreaLinePoints,
  returnAreaLinesToUnselectedState,
  selectHoveredAreaLinePoints,
  selectAreaLinePoints,
} from 'utils/charts/area';
import chartLegendTitleSelector from 'selectors/chartLegendTitleSelector';
import useSelectorWithProps from 'hooks/useSelectorWithProps';
import genericChartCapsuleSelector from 'selectors/genericChartCapsuleSelector';
import ChartImpl from './ChartImpl';
import ChartLegend from './ChartLegend';

const propTypes = {
  blockId: PropTypes.string.isRequired,
  boardId: PropTypes.number.isRequired,
  perspectives: PropTypes.object,
  privacy: PropTypes.oneOf(['private', 'public']),
  scenarioId: PropTypes.number.isRequired,
  showLegend: PropTypes.bool,
  workspaceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

const defaultProps = {
  perspectives: undefined,
  privacy: 'private',
  showLegend: false,
};

const StackedAreaChart = ({
  blockId,
  boardId,
  perspectives,
  privacy,
  scenarioId,
  showLegend,
  workspaceId,
  ...restProps
}) => {
  const title = useSelectorWithProps(chartLegendTitleSelector, {
    blockId,
    public: privacy === 'public',
    workspaceId,
  });

  const { options, series, xUnit } = useSelectorWithProps(
    genericChartCapsuleSelector,
    {
      blockId,
      boardId,
      perspectives,
      public: privacy === 'public',
      scenarioId,
      workspaceId,
    },
  );

  const handleSelectPoints = useCallback((chart) => {
    if (chart.hoverPoints) {
      deselectAreaLinePoints(chart);
      selectHoveredAreaLinePoints(chart);
    }
  }, []);

  const handleSelectPointValue = useCallback((chart, value) => {
    if (value) {
      selectAreaLinePoints(chart, value);
    } else {
      returnAreaLinesToUnselectedState(
        flatten(chart.series.map((s) => s.points)),
        chart,
      );
    }
    chart.redraw();
  }, []);

  return (
    <ChartImpl
      formatLabel={xUnit.format}
      legend={
        showLegend ? (
          <ChartLegend icon='line' series={series} title={title} />
        ) : undefined
      }
      onExternalClick={returnAreaLinesToUnselectedState}
      onSelectPoints={handleSelectPoints}
      onSelectPointValue={handleSelectPointValue}
      options={options}
      {...restProps}
    />
  );
};

StackedAreaChart.propTypes = propTypes;
StackedAreaChart.defaultProps = defaultProps;

export default StackedAreaChart;
